import React, { useState, useEffect } from "react";
import { isMobile } from 'react-device-detect';
import { Input, Typography, ConfigProvider, Form, Button, Space, Flex, Image } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { InputPhoneNumber, phoneNumberIsMatch, routes, SMSVerification, SuccessIcon, GoBackButton } from "../components";
import { sendSMSCode, updatePassword, userExists } from "../components/api";
import { successValidationStatus, errorValidationStatus } from '../components/antd';
import { checkPassword, passwordMinLength, getSpecialCharacters as getPasswordSpecialCharacters } from '../components/password';
import { passwordRecoverySteps, defaultPasswordRecoverySteps } from '../components/passwordRecovery';
import { getPageTitleTheme, getPageDescriptionTheme, getMobileSendSMSFormTheme } from '../styles/pages/passwordRecovery';
import "../styles/password-recovery-mobile.scss";
import "../styles/password-recovery.scss";

const { Text, Title } = Typography;

const PageTitle = ({ text }) => (
    <ConfigProvider theme={getPageTitleTheme()}>
        <Title>{text}</Title>
    </ConfigProvider>
);

const PageDescription = ({ text }) => (
    <ConfigProvider theme={getPageDescriptionTheme()}>
        <Text>{text}</Text>
    </ConfigProvider>
);

const SendSMSWithCodeStep = ({ onSMSSended, onGoBack }) => {
    const [form] = Form.useForm();

    const phoneNumberErrorText = 'Введите номер телефона';
    const [phoneNumberValidationStatus, setPhoneNumberValidationStatus] = useState(successValidationStatus);
    const [phoneNumberHelpText, setPhoneNumberHelpText] = useState(null);

    useEffect(() => {
        if (phoneNumberValidationStatus === errorValidationStatus)
            setPhoneNumberHelpText(phoneNumberErrorText);
        else
            setPhoneNumberHelpText(null);
    }, [phoneNumberValidationStatus]);

    const getFormatedPhone = (inputPhone) => `7${inputPhone.replace(/[^0-9+]/g, '')}`;

    const handleFormFinish = (fieldsValue) => {
        if (fieldsValue.phoneNumber && phoneNumberIsMatch(fieldsValue.phoneNumber)) {
            setPhoneNumberValidationStatus(successValidationStatus);

            const formatedPhoneNumber = getFormatedPhone(fieldsValue.phoneNumber);

            userExists({ phoneNumber: formatedPhoneNumber }).then(
                () => {
                    setPhoneNumberValidationStatus(successValidationStatus);
                    onSMSSended(`+7 ${fieldsValue.phoneNumber}`, formatedPhoneNumber);
                },
                (error) => setPhoneNumberValidationStatus(errorValidationStatus)
            );
        }
        else {
            setPhoneNumberValidationStatus(errorValidationStatus);
        }
    }

    const PhoneNumber = () => (
        <div className={isMobile ? 'password-recovery-mobile__step__sens-SMS__phone' : ''}>
            <Form.Item
                name="phoneNumber"
                validateStatus={phoneNumberValidationStatus}
                help={phoneNumberHelpText}
            >
                <InputPhoneNumber size="large" />
            </Form.Item>
        </div>
    );

    const SendSMSButton = () => (
        <Form.Item>
            <Flex vertical align="stretch">
                <Button type="primary" htmlType="submit" size="large" >Получить СМС с кодом</Button>
            </Flex>
        </Form.Item>
    );

    return (
        <>
            <ConfigProvider theme={getMobileSendSMSFormTheme()}>
                <PageTitle text="Восстановить пароль" />

                <Form
                    form={form}
                    onFinish={handleFormFinish}
                >
                    <Space vertical size="small">
                        <PageDescription text="Чтобы сбросить пароль, введите свой номер телефона, который мог использоваться в системе" />
                        <PhoneNumber />
                    </Space>

                    <SendSMSButton />
                </Form>
            </ConfigProvider>
        </>
    );
}

const InputVerifyCodeStep = ({ inputedPhoneNumber, formatedPhoneNumber, onSMSVerified, onGoBack }) => (
    <SMSVerification
        pageTitleTheme={getPageTitleTheme()}
        pageDescriptionTheme={getPageDescriptionTheme()}
        phoneNumberRepresentation={inputedPhoneNumber}
        formatedPhoneNumber={formatedPhoneNumber}
        onRightInputedCode={onSMSVerified}
        buttonClassName={isMobile ? 'password-recovery-mobile__step__button' : 'password-recovery__step__button'}
    />
)

const SetNewPasswordStep = ({ formatedPhoneNumber, onPasswordChanged }) => {
    const [form] = Form.useForm();

    const [passwordValidationStatus, setPasswordValidationStatus] = useState(successValidationStatus);

    const repeatedPasswordErrorText = 'Пароли не совпадают';
    const [repeatedPasswordValidationStatus, setRepeatedPasswordValidationStatus] = useState(successValidationStatus);
    const [repeatedPasswordHelpText, setRepeatedPasswordHelpText] = useState();

    useEffect(() => {
        if (repeatedPasswordValidationStatus === errorValidationStatus)
            setRepeatedPasswordHelpText(repeatedPasswordErrorText);
        else
            setRepeatedPasswordHelpText();
    }, [repeatedPasswordValidationStatus]);

    const handleFormFinish = (fieldsValue) => {
        if (checkPassword(fieldsValue.password)) {
            setPasswordValidationStatus(successValidationStatus);
        }
        else {
            setPasswordValidationStatus(errorValidationStatus);

            return;
        }

        if (fieldsValue.password === fieldsValue.repeatedPassword) {
            setRepeatedPasswordValidationStatus(successValidationStatus);
        }
        else {
            setRepeatedPasswordValidationStatus(errorValidationStatus);

            return;
        }

        updatePassword({ phoneNumber: formatedPhoneNumber, password: fieldsValue.password })
            .then(
                () => onPasswordChanged(),
                (error) => { }
            );
    }

    return (
        <>
            <PageTitle text="Восстановить пароль" />

            <Form
                form={form}
                onFinish={handleFormFinish}
                layout="vertical"
            >
                <Form.Item
                    name="password"
                    label="Пароль"
                    validateStatus={passwordValidationStatus}
                    help={`Как минимум ${passwordMinLength} символов: заглавные и строчные латинские буквы, цифры и спецсимволы (${(getPasswordSpecialCharacters().join(', '))})`}
                >
                    <Input.Password
                        size="large"
                        placeholder="Задайте пароль"
                    />
                </Form.Item>

                <Form.Item
                    name="repeatedPassword"
                    label="Повтор пароля"
                    validateStatus={repeatedPasswordValidationStatus}
                    help={repeatedPasswordHelpText}
                >
                    <Input.Password
                        size="large"
                        placeholder="Повторите пароль"
                    />
                </Form.Item>

                <Form.Item>
                    <Flex vertical align="stretch">
                        <Button type="primary" htmlType="submit" size="large" >Сохранить</Button>
                    </Flex>
                </Form.Item>
            </Form>
        </>
    );
}

const SuccessStep = ({ onGoToMainPage }) => {
    return (
        <Flex vertical gap="20px" align="stretch" className={isMobile ? 'password-recovery-mobile__step__success' : ''}>
            <SuccessIcon />

            <PageTitle text='Успех!' />
            <PageDescription text='Пароль был успешно изменён' />

            <Flex vertical align="stretch">
                <Button htmlType="button" type="primary" size="large" onClick={onGoToMainPage} >Войти</Button>
            </Flex>
        </Flex>
    );
}

export const PasswordRecovery = () => {
    const [currentStep, setCurrentStep] = useState(defaultPasswordRecoverySteps);

    const [inputedPhoneNumber, setInputedPhoneNumber] = useState();
    const [formatedPhoneNumber, setFormatedPhoneNumber] = useState();

    useEffect(() => {
        if (currentStep === passwordRecoverySteps.sendSMSWithCode) {
            if (inputedPhoneNumber)
                setInputedPhoneNumber();

            if (formatedPhoneNumber)
                setFormatedPhoneNumber();
        }
        else if (currentStep === passwordRecoverySteps.verifyPhoneNumber) {
            sendSMSCode({ phone: formatedPhoneNumber }).then(
                () => { },
                (error) => { }
            );
        }
        else if (currentStep === passwordRecoverySteps.setNewPassword) {
            setInputedPhoneNumber();
            setFormatedPhoneNumber();
        }
    }, [formatedPhoneNumber]);

    const goToNextStep = () => {
        if (currentStep === passwordRecoverySteps.sendSMSWithCode) {
            setCurrentStep(passwordRecoverySteps.verifyPhoneNumber);
        }

        else if (currentStep === passwordRecoverySteps.verifyPhoneNumber) {
            setCurrentStep(passwordRecoverySteps.setNewPassword);
        }

        else if (currentStep === passwordRecoverySteps.setNewPassword) {
            setCurrentStep(passwordRecoverySteps.completed);
        }

        else if (currentStep === passwordRecoverySteps.completed) {
            window.location.href = routes.authorization;
        }
    }

    const goToPreviousStep = () => {
        if (currentStep === passwordRecoverySteps.sendSMSWithCode) {
            window.location.href = routes.authorization;
        }
        else if (currentStep === passwordRecoverySteps.verifyPhoneNumber) {
            setCurrentStep(passwordRecoverySteps.sendSMSWithCode);
        }
        else if (currentStep === passwordRecoverySteps.setNewPassword) {
            setCurrentStep(passwordRecoverySteps.sendSMSWithCode);
        }
    }

    const handleSMSSended = (inputedPhoneNumber, formatedPhoneNumber) => {
        setInputedPhoneNumber(inputedPhoneNumber);
        setFormatedPhoneNumber(formatedPhoneNumber);

        goToNextStep();
    }

    const GoBackButtonInt = () => (
        <GoBackButton onClick={goToPreviousStep} />
    );

    const Container = ({ children }) => (
        isMobile
            ?
            (
                <Flex vertical gap="20px" align="center" className="password-recovery-mobile__main">
                    <Flex justify="start" className="password-recovery-mobile__navigation">
                        {currentStep !== passwordRecoverySteps.completed
                            && <GoBackButtonInt />
                        }
                    </Flex>
                    <Flex vertical justify="start" align="stretch" gap="24px" className="password-recovery-mobile__step">
                        {children}
                    </Flex>
                </Flex>
            )
            :
            (
                <Flex vertical align="center" justify="center" className="password-recovery__main">
                    <Flex vertical gap="small" align="center" justify="center" className="password-recovery__interaction-window">
                        <Flex justify="start" className="password-recovery__navigation">
                            {currentStep !== passwordRecoverySteps.completed
                                && <GoBackButtonInt />
                            }
                        </Flex>
                        <Flex vertical justify="center" align="center" gap="24px" className="password-recovery__step">
                            {children}
                        </Flex>
                    </Flex>
                </Flex>
            )
    );

    return (
        <Container>
            {currentStep === passwordRecoverySteps.sendSMSWithCode &&
                <SendSMSWithCodeStep
                    onSMSSended={handleSMSSended}
                    onGoBack={goToPreviousStep}
                />
            }

            {currentStep === passwordRecoverySteps.verifyPhoneNumber &&
                <InputVerifyCodeStep
                    onSMSVerified={goToNextStep}
                    onGoBack={goToPreviousStep}
                    inputedPhoneNumber={inputedPhoneNumber}
                    formatedPhoneNumber={formatedPhoneNumber}
                />
            }

            {currentStep === passwordRecoverySteps.setNewPassword &&
                <SetNewPasswordStep
                    formatedPhoneNumber={formatedPhoneNumber}
                    onPasswordChanged={goToNextStep}
                />
            }

            {currentStep === passwordRecoverySteps.completed &&
                <SuccessStep onGoToMainPage={goToNextStep} />
            }
        </Container>
    );
}